import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import useTvApi, { messageOf } from "hooks/useTvApi";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import TvTargetPicker, { useTargetableTvs } from "pages/TvPage/TvTargetPicker";
import {
  DEFAULT_SLIDE_SECONDS,
  normalizeSeconds,
} from "pages/PostersPage/tvOrder";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  SaveBar,
  SegmentedControl,
  Skeleton,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  Poster,
  TvScheduleDisplayMode,
  TvScheduleRule,
  TvSettings,
} from "types/TvPosterTypes";
import {
  activeRule,
  appliesOn,
  DAY_SHORT,
  DISPLAY_MODES,
  displayModeOf,
  hasErrors,
  move,
  newRule,
  osloMoment,
  overlappingRule,
  summary,
  toMinutes,
  validateRule,
  validTimes,
  weekDates,
} from "./tvSchedule";
import styles from "./TvSchedulePage.module.scss";

/*
 * Visningsplan: «tidsstyrte visninger» som viser bare utvalgte plakater på
 * faste dager og tider. Rekkefølgen i listen er prioriteten.
 */

type EditRule = TvScheduleRule & { key: string };

let keySeq = 0;
const withKey = (rule: TvScheduleRule): EditRule => ({
  ...rule,
  displayMode: displayModeOf(rule),
  tvDeviceIds: rule.tvDeviceIds ?? [],
  key: `r${++keySeq}`,
});
const stripKey = ({ key, ...rule }: EditRule): TvScheduleRule => rule;
const snapshot = (rules: EditRule[]) => JSON.stringify(rules.map(stripKey));

const RULE_COLORS = ["#1D7A52", "#2B5C93", "#A0532C", "#6B4FA3"];
const colorOf = (index: number) => RULE_COLORS[index % RULE_COLORS.length];

/** Liten skisse av TV-en for hver visningsmåte. */
const ModeSketch: React.FC<{ mode: TvScheduleDisplayMode }> = ({ mode }) => (
  <svg
    className={styles.sketch}
    viewBox="0 0 64 36"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      className={styles.sketchFrame}
      x="0.5"
      y="0.5"
      width="63"
      height="35"
      rx="3"
    />
    {mode === "POSTER_WITH_PRAYERS" && (
      <>
        <rect
          className={styles.sketchPoster}
          x="4"
          y="4"
          width="56"
          height="21"
          rx="1.5"
        />
        <rect
          className={styles.sketchStrip}
          x="4"
          y="28"
          width="44"
          height="4"
          rx="1"
        />
        <rect
          className={styles.sketchAccent}
          x="50"
          y="28"
          width="10"
          height="4"
          rx="1"
        />
      </>
    )}
    {mode === "POSTER_ONLY" && (
      <rect
        className={styles.sketchPoster}
        x="2"
        y="2"
        width="60"
        height="32"
        rx="2"
      />
    )}
    {mode === "CAROUSEL" && (
      <>
        <rect
          className={styles.sketchAccent}
          x="5"
          y="6"
          width="14"
          height="5"
          rx="1"
        />
        {[14, 18.5, 23, 27.5].map((y) => (
          <rect
            key={y}
            className={styles.sketchStrip}
            x="5"
            y={y}
            width="18"
            height="2.6"
            rx="1"
          />
        ))}
        <rect
          className={styles.sketchPoster}
          x="28"
          y="4"
          width="32"
          height="28"
          rx="1.5"
        />
      </>
    )}
  </svg>
);

/** Hva TV-en viser for hver visningsmåte («Slik bestemmes det som vises»). */
const modeHow: Record<TvScheduleDisplayMode, string> = {
  POSTER_WITH_PRAYERS:
    "plakaten så stort som mulig, med bønnetidene (salah og jamat) og nedtelling nederst.",
  POSTER_ONLY: "plakaten i hele skjermen, uten klokke og bønnetider.",
  CAROUSEL:
    "TV-ens vanlige visning med klokke og bønnetider, men karusellen viser bare plakatene i visningen.",
};

const H0 = 6;
const H1 = 24;
const HOUR_PX = 22;

const TvSchedulePage: React.FC = () => {
  const { selectedOrgNumber: orgNr } = useAuth();
  const tvs = useTargetableTvs(orgNr);
  const api = useTvApi();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [rules, setRules] = useState<EditRule[]>([]);
  const [saved, setSaved] = useState("[]");
  const [posters, setPosters] = useState<Poster[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [now, setNow] = useState(() => osloMoment());
  const [slideSeconds, setSlideSeconds] = useState(DEFAULT_SLIDE_SECONDS);

  useEffect(() => {
    if (!orgNr) return;
    api<TvSettings>(`/api/v1/mosques/${orgNr}/tv-settings`, "GET")
      .then((res) => setSlideSeconds(normalizeSeconds(res?.slideSeconds)))
      .catch(() => undefined);
  }, [api, orgNr]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(osloMoment()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    if (!orgNr) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [schedule, posterList] = await Promise.all([
        api<TvScheduleRule[]>(`/api/v1/mosques/${orgNr}/tv-schedule`, "GET"),
        api<Poster[]>(`/api/v1/poster/org/${orgNr}`, "GET"),
      ]);
      const list = (Array.isArray(schedule) ? schedule : []).map(withKey);
      setRules(list);
      setSaved(snapshot(list));
      setPosters(Array.isArray(posterList) ? posterList : []);
    } catch (e) {
      setLoadError(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, [api, orgNr]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = !loading && snapshot(rules) !== saved;
  const posterById = useMemo(
    () => new Map(posters.map((p) => [p.id, p])),
    [posters],
  );
  const posterName = (id: number) => posterById.get(id)?.tittel;
  const active = activeRule(rules, now);
  const errors = rules.map(validateRule);

  const update = (key: string, patch: Partial<TvScheduleRule>) =>
    setRules((list) =>
      list.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );

  const togglePoster = (rule: EditRule, id: number) =>
    update(rule.key, {
      posterIds: rule.posterIds.includes(id)
        ? rule.posterIds.filter((p) => p !== id)
        : [...rule.posterIds, id],
    });

  const toggleDay = (rule: EditRule, day: number) =>
    update(rule.key, {
      days: rule.days.includes(day)
        ? rule.days.filter((d) => d !== day)
        : [...rule.days, day].sort((a, b) => a - b),
    });

  const remove = async (rule: EditRule) => {
    const ok = await confirm({
      title: `Slett «${rule.name || "visningen"}»?`,
      message: "Visningen fjernes fra planen når du lagrer.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (ok) setRules((list) => list.filter((r) => r.key !== rule.key));
  };

  const add = () => {
    setRules((list) => [...list, withKey(newRule())]);
  };

  const save = async () => {
    if (errors.some(hasErrors)) {
      setShowErrors(true);
      toast.error("Rett feilene i visningsplanen før du lagrer.");
      return;
    }
    setSaving(true);
    try {
      const body = rules.map((r) => ({ ...stripKey(r), name: r.name.trim() }));
      const result = await api<TvScheduleRule[]>(
        `/api/v1/mosques/${orgNr}/tv-schedule`,
        "PUT",
        body,
      );
      const list = (Array.isArray(result) ? result : body).map(withKey);
      setRules(list);
      setSaved(snapshot(list));
      setShowErrors(false);
      toast.success("Visningsplanen er lagret.");
    } catch (e) {
      toast.error(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setRules((JSON.parse(saved) as TvScheduleRule[]).map(withKey));
    setShowErrors(false);
  };

  const week = weekDates(now.date);

  const ruleCard = (rule: EditRule, index: number) => {
    const err = showErrors ? errors[index] : {};
    const timeError =
      errors[index].time === "Til-tiden må være etter fra-tiden."
        ? errors[index].time
        : err.time;
    const overlap = overlappingRule(rules, index);
    return (
      <article
        key={rule.key}
        className={styles.rule}
        style={{ "--rc": colorOf(index) } as React.CSSProperties}
      >
        <div className={styles.ruleHead}>
          <TextField
            aria-label="Navn på visningen"
            className={styles.ruleName}
            fieldClassName={styles.ruleNameField}
            value={rule.name}
            maxLength={80}
            onChange={(e) => update(rule.key, { name: e.target.value })}
            error={err.name}
          />
          {active?.key === rule.key && <Badge tone="mint">Aktiv nå</Badge>}
          <span className={styles.ruleActs}>
            <IconButton
              size="sm"
              label="Flytt opp"
              disabled={index === 0}
              icon={
                <span className={styles.rotate}>
                  <Icon name="chevronLeft" size={16} strokeWidth={2} />
                </span>
              }
              onClick={() => setRules((list) => move(list, index, -1))}
            />
            <IconButton
              size="sm"
              label="Flytt ned"
              disabled={index === rules.length - 1}
              icon={
                <span className={styles.rotate}>
                  <Icon name="chevronRight" size={16} strokeWidth={2} />
                </span>
              }
              onClick={() => setRules((list) => move(list, index, 1))}
            />
            <IconButton
              size="sm"
              label="Slett"
              icon={<Icon name="trash" size={16} strokeWidth={1.9} />}
              onClick={() => remove(rule)}
            />
          </span>
        </div>

        <div className={styles.ruleRow}>
          <span className={styles.ruleLabel}>Plakater</span>
          <div>
            {posters.length === 0 ? (
              <span className={styles.muted}>
                Ingen plakater ennå. Lag eller last opp en under «Plakater på
                TV».
              </span>
            ) : (
              <div className={styles.chips}>
                {posters.map((p) => {
                  const order = rule.posterIds.indexOf(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={styles.chip}
                      aria-pressed={order >= 0}
                      onClick={() => togglePoster(rule, p.id)}
                    >
                      {p.plakat && <img src={p.plakat} alt="" />}
                      {order >= 0 && (
                        <span className={styles.chipOrder}>{order + 1}</span>
                      )}
                      {p.tittel || `Plakat ${p.id}`}
                    </button>
                  );
                })}
              </div>
            )}
            {err.posters && (
              <span className={styles.error} role="alert">
                {err.posters}
              </span>
            )}
          </div>
        </div>

        <div className={styles.ruleRow}>
          <span className={styles.ruleLabel}>Når</span>
          <div className={styles.when}>
            <SegmentedControl<TvScheduleRule["repeat"]>
              label="Gjentakelse"
              value={rule.repeat}
              onChange={(repeat) => update(rule.key, { repeat })}
              options={[
                { value: "WEEKLY", label: "Hver uke" },
                { value: "DATE", label: "Én dato" },
              ]}
            />
            {rule.repeat === "WEEKLY" ? (
              <div className={styles.chips} role="group" aria-label="Dager">
                {DAY_SHORT.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    className={styles.chip}
                    aria-pressed={rule.days.includes(i + 1)}
                    onClick={() => toggleDay(rule, i + 1)}
                  >
                    {d}
                  </button>
                ))}
              </div>
            ) : (
              <TextField
                aria-label="Dato"
                type="date"
                fieldClassName={styles.dateField}
                value={rule.date ?? ""}
                onChange={(e) =>
                  update(rule.key, { date: e.target.value || null })
                }
              />
            )}
            {err.when && (
              <span className={styles.error} role="alert">
                {err.when}
              </span>
            )}
          </div>
        </div>

        <div className={styles.ruleRow}>
          <span className={styles.ruleLabel}>Tid</span>
          <div>
            <div className={styles.times}>
              <TextField
                aria-label="Fra"
                type="time"
                value={rule.from}
                onChange={(e) => update(rule.key, { from: e.target.value })}
              />
              <span aria-hidden="true">–</span>
              <TextField
                aria-label="Til"
                type="time"
                value={rule.to}
                onChange={(e) => update(rule.key, { to: e.target.value })}
              />
            </div>
            {timeError && (
              <span className={styles.error} role="alert">
                {timeError}
              </span>
            )}
          </div>
        </div>

        {tvs.length > 1 && (
          <div className={styles.ruleRow}>
            <span className={styles.ruleLabel}>TV-er</span>
            <TvTargetPicker
              devices={tvs}
              value={rule.tvDeviceIds}
              label={`TV-er for «${rule.name || "visningen"}»`}
              onChange={(tvDeviceIds) => update(rule.key, { tvDeviceIds })}
            />
          </div>
        )}

        <div className={styles.ruleRow}>
          <span className={styles.ruleLabel}>Slik vises den</span>
          <div
            className={styles.modes}
            role="radiogroup"
            aria-label={`Slik vises «${rule.name || "visningen"}»`}
          >
            {DISPLAY_MODES.map((m) => {
              const on = displayModeOf(rule) === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`${styles.mode} ${on ? styles.modeOn : ""}`}
                  onClick={() => update(rule.key, { displayMode: m.value })}
                >
                  <ModeSketch mode={m.value} />
                  <b>{m.label}</b>
                  <span>{m.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        {overlap && (
          <Alert tone="warn">
            Overlapper med «{overlap.name}», som står over og gjelder i den
            tiden.
          </Alert>
        )}
        <p className={styles.summary}>{summary(rule, posterName)}</p>
      </article>
    );
  };

  const weekView = (
    <div className={styles.week}>
      <div className={styles.weekGrid}>
        <span />
        {week.map((date, i) => (
          <span
            key={date}
            className={`${styles.weekHead} ${
              date === now.date ? styles.weekToday : ""
            }`}
          >
            {DAY_SHORT[i]} {+date.slice(8)}.
          </span>
        ))}
        <div
          className={styles.weekHours}
          style={{ height: (H1 - H0) * HOUR_PX }}
        >
          {Array.from({ length: (H1 - H0) / 2 + 1 }, (_, i) => (
            <span key={i} style={{ top: i * 2 * HOUR_PX }}>
              {String(H0 + i * 2).padStart(2, "0")}
            </span>
          ))}
        </div>
        {week.map((date) => (
          <div
            key={date}
            className={`${styles.weekDay} ${
              date === now.date ? styles.weekDayToday : ""
            }`}
            style={{ height: (H1 - H0) * HOUR_PX }}
          >
            {rules.map((rule, i) => {
              if (!appliesOn(rule, date) || !validTimes(rule)) return null;
              const top = Math.max(
                0,
                (toMinutes(rule.from)! / 60 - H0) * HOUR_PX,
              );
              const bottom = Math.min(
                (H1 - H0) * HOUR_PX,
                (toMinutes(rule.to)! / 60 - H0) * HOUR_PX,
              );
              if (bottom <= top) return null;
              return (
                <span
                  key={rule.key}
                  className={`${styles.weekBlock} ${
                    rule.posterIds.length === 0 ? styles.weekBlockEmpty : ""
                  }`}
                  style={
                    {
                      "--rc": colorOf(i),
                      top,
                      height: bottom - top,
                      left: 2 + Math.min(i, 3) * 4,
                      zIndex: rules.length - i,
                    } as React.CSSProperties
                  }
                  title={`${rule.name} ${rule.from}–${rule.to}`}
                >
                  {rule.name}
                </span>
              );
            })}
            {date === now.date && now.minutes >= H0 * 60 && (
              <span
                className={styles.weekNow}
                style={{ top: (now.minutes / 60 - H0) * HOUR_PX }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Visningsplan"
        subtitle="Bestem hvilke plakater TV-en viser på faste dager og tider."
      />
      <SectionTabs />

      {loadError ? (
        <Alert tone="error">
          {loadError}{" "}
          <button type="button" className={styles.linkBtn} onClick={load}>
            Prøv igjen
          </button>
        </Alert>
      ) : (
        <div className={styles.layout}>
          <Card
            title="Tidsstyrte visninger"
            subtitle="På valgte dager og tider vises bare plakatene du velger her. Øverst i listen gjelder først."
            actions={
              <Button
                size="sm"
                icon={<Icon name="plus" size={14} strokeWidth={2} />}
                onClick={add}
                disabled={loading}
              >
                Ny tidsstyrt visning
              </Button>
            }
          >
            {loading ? (
              <div className={styles.rules}>
                <Skeleton height={260} radius={18} />
                <Skeleton height={260} radius={18} />
              </div>
            ) : rules.length === 0 ? (
              <EmptyState
                icon="cal"
                title="Ingen tidsstyrte visninger"
                action={
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Icon name="plus" size={14} strokeWidth={2} />}
                    onClick={add}
                  >
                    Ny tidsstyrt visning
                  </Button>
                }
              >
                TV-en viser plakatene med «Vis på TV» som vanlig.
              </EmptyState>
            ) : (
              <div className={styles.rules}>{rules.map(ruleCard)}</div>
            )}
          </Card>

          <div className={styles.side}>
            <Card
              title="Denne uken"
              subtitle={
                active
                  ? `Nå vises «${active.name}».`
                  : "Nå vises plakatene med «Vis på TV» som vanlig."
              }
            >
              {loading ? <Skeleton height={300} radius={12} /> : weekView}
            </Card>
            <Card title="Slik bestemmes det som vises">
              <ol className={styles.how}>
                <li>
                  <b>En tidsstyrt visning er aktiv:</b> bare plakatene i den
                  vises, i rekkefølge, {slideSeconds} sekunder hver. Hvordan de
                  vises, velger du under «Slik vises den»:
                  <ul className={styles.howModes}>
                    {DISPLAY_MODES.map((m) => {
                      const users = rules
                        .filter((r) => displayModeOf(r) === m.value)
                        .map((r) => `«${r.name || "uten navn"}»`);
                      return (
                        <li key={m.value}>
                          <b>{m.label}:</b> {modeHow[m.value]}
                          {users.length > 0 && (
                            <span className={styles.muted}>
                              {" "}
                              Brukes av {users.join(", ")}.
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
                <li>
                  <b>To visninger overlapper:</b> den som står øverst i listen
                  gjelder. Flytt med pilene for å endre rekkefølgen.
                </li>
                <li>
                  <b>Bønn går alltid foran:</b> ved adhan, iqamah og jummah
                  viser TV-en bønnevarselet, uansett visningsmåte og også når en
                  tidsstyrt visning er aktiv.
                </li>
                <li>
                  <b>Ingen tidsstyrt visning:</b> plakatene med «Vis på TV»
                  bytter på som vanlig.
                </li>
              </ol>
              <span className={styles.muted}>Tidene følger norsk tid.</span>
              {posters.length === 0 && !loading && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(RoutePaths.TvStudio)}
                >
                  Lag en plakat
                </Button>
              )}
            </Card>
          </div>
        </div>
      )}

      <SaveBar
        visible={dirty}
        onSave={save}
        onDiscard={discard}
        saving={saving}
        saveLabel="Lagre visningsplan"
      />
    </>
  );
};

export default TvSchedulePage;
